import styled, { css } from 'styled-components';

import { ReactComponent as LoadingIcon } from '@pkg/assets/icons/v2/loading-bars.svg';

import { ButtonBase, type Color, Colors, Variants } from '../ButtonBase';

import { type Size, Sizes } from './types';

const BUTTON_BREAKPOINT = 'mobile';

export type StyledButtonProps = {
    disabled?: boolean;
    $circle?: boolean;
    $fluid?: boolean;
    $gutterBottom?: boolean;
    $size: Size;
    $loading?: boolean;
    $hasIcon?: boolean;
    $color?: Color;
};

export const StyledButton = styled(ButtonBase)<StyledButtonProps>`
    &&& {
        font-family: 'Alliance No.1';
        font-style: normal;
        font-weight: 700;

        ${({ theme, $size, $hasIcon, $color }) => {
            switch ($size) {
                case Sizes.large: {
                    return css`
                        min-height: 56px;
                        padding: ${$hasIcon ? '0 24px' : '0 32px'};
                        border-radius: 40px;

                        font-size: 0.875rem;
                        line-height: 1.4285714286;
                        letter-spacing: -0.2px;

                        @media (min-width: ${theme.breakpoints[BUTTON_BREAKPOINT]}) {
                            min-height: 80px;
                            padding: ${$hasIcon ? '0 40px' : '0 48px'};
                            border-radius: 48px;

                            font-size: 1.125rem;
                            line-height: 1.3333333333;
                            letter-spacing: -0.5px;
                        }

                        ${$color === 'primary' &&
                        css`
                            box-shadow:
                                0 0 30px ${theme.shades.purple[60]},
                                inset 0 0 54px ${theme.colors.purple};
                        `}
                    `;
                }
                case Sizes.medium50: {
                    return css`
                        min-height: 50px;
                        padding: ${$hasIcon ? '0 25px' : '0 24px'};
                        border-radius: 40px;

                        font-size: 0.875rem;
                        line-height: 1.42857143;
                        letter-spacing: -0.2px;

                        ${$color === 'primary' &&
                        css`
                            box-shadow:
                                0 0 24px ${theme.shades.purple[60]},
                                inset 0 0 32px ${theme.colors.purple};
                        `}
                    `;
                }
                case Sizes.medium: {
                    return css`
                        min-height: 44px;
                        padding: ${$hasIcon ? '0 16px' : '0 24px'};
                        border-radius: 40px;

                        font-size: 0.875rem;
                        line-height: 1.4285714286;
                        letter-spacing: -0.2px;

                        @media (min-width: ${theme.breakpoints[BUTTON_BREAKPOINT]}) {
                            min-height: 56px;
                            padding: ${$hasIcon ? '0 24px' : '0 32px'};
                            border-radius: 40px;

                            font-size: 1.125rem;
                            line-height: 1.3333333333;
                            letter-spacing: -0.5px;
                        }

                        ${$color === 'primary' &&
                        css`
                            box-shadow:
                                0 0 16px ${theme.shades.purple[60]},
                                inset 0 0 24px ${theme.colors.purple};
                        `}
                    `;
                }
                case Sizes.small: {
                    return css`
                        min-height: 32px;
                        padding: ${$hasIcon ? '0 16px' : '0 16px'};
                        border-radius: 40px;

                        font-size: 0.75rem;
                        line-height: 1.3333333333;
                        letter-spacing: -0.1px;

                        @media (min-width: ${theme.breakpoints[BUTTON_BREAKPOINT]}) {
                            min-height: 40px;
                            padding: ${$hasIcon ? '0 16px' : '0 24px'};
                            border-radius: 40px;

                            font-size: 0.875rem;
                            line-height: 1.4285714286;
                            letter-spacing: -0.2px;
                        }

                        ${$color === 'primary' &&
                        css`
                            box-shadow:
                                0 0 16px ${theme.shades.purple[60]},
                                inset 0 0 24px ${theme.colors.purple};
                        `}
                    `;
                }
                case Sizes.xsmall: {
                    return css`
                        min-height: 24px;
                        padding: ${$hasIcon ? '0 8px' : '0 12px'};
                        border-radius: 40px;

                        font-size: 0.35rem;
                        line-height: 1.1;
                        letter-spacing: -0.1px;

                        @media (min-width: ${theme.breakpoints[BUTTON_BREAKPOINT]}) {
                            min-height: 30px;
                            padding: ${$hasIcon ? '0 10px' : '0 14px'};
                            border-radius: 40px;

                            font-size: 0.75rem;
                            line-height: 1.15;
                            letter-spacing: -0.15px;
                        }

                        ${$color === 'primary' &&
                        css`
                            box-shadow:
                                0 0 16px ${theme.shades.purple[60]},
                                inset 0 0 24px ${theme.colors.purple};
                        `}
                    `;
                }
            }
        }}

        color: ${({ $loading }) => ($loading ? 'transparent' : '')};

        width: ${({ $fluid }) => ($fluid ? '100%' : 'auto')};

        ${({ $gutterBottom, theme }) =>
            $gutterBottom &&
            css`
                margin-bottom: 20px;

                @media (min-width: ${theme.breakpoints[BUTTON_BREAKPOINT]}) {
                    margin-bottom: 30px;
                }
            `}

        ${({ variant }) =>
            variant === Variants.foggy
                ? css`
                      box-shadow: none;
                  `
                : null}

        ${({ variant }) =>
            variant === Variants.highlight
                ? css`
                      transition: box-shadow 300ms ease;
                      box-shadow: none;

                      &:hover {
                          box-shadow: 0 0 24px rgba(94, 69, 242, 0.7);
                      }
                  `
                : null}
        ${({ $color, disabled }) =>
            $color === Colors.clear &&
            !disabled &&
            css`
                &:hover {
                    opacity: 0.75;
                }
            `}
`;

export const StyledLoadingWrapper = styled.div`
    &&& {
        position: absolute;
        visibility: visible;
        display: flex;
        left: 50%;
        transform: translate(-50%);
        color: ${({ theme }) => theme.shades.white[50]};
    }
`;

export const StyledLoadingIcon = styled(LoadingIcon)`
    &&& {
        display: inline-block;
        flex-shrink: 0;
        vertical-align: middle;
        height: auto;
        overflow: hidden;
        fill: currentColor;
        box-sizing: border-box;
        width: 1em;
        height: 1em;
        user-select: none;
        font-size: 2rem;
    }
`;

type StyledIconProps = {
    $size: Size;
    $loading?: boolean;
};

const StyledIcon = styled.span<StyledIconProps>`
    &&& {
        display: inherit;
        box-sizing: border-box;
        user-select: none;
        opacity: ${({ $loading }) => ($loading ? 0 : 1)};

        svg {
            display: inline-block;
            flex-shrink: 0;
            vertical-align: middle;
            max-width: 100%;
            overflow: hidden;
            fill: currentColor;
            box-sizing: inherit;
            width: 1em;
            height: 1em;
            user-select: none;
        }
        path {
            fill: currentColor; // So we can change the color of the svg. Ideally the svg should not have fill property defined.
        }

        > :nth-of-type(1) {
            ${({ theme, $size }) => {
                switch ($size) {
                    case Sizes.large: {
                        return css`
                            font-size: 1.5rem;

                            @media (min-width: ${theme.breakpoints[BUTTON_BREAKPOINT]}) {
                                font-size: 2rem;
                            }
                        `;
                    }

                    case Sizes.medium: {
                        return css`
                            font-size: 1.125rem;

                            @media (min-width: ${theme.breakpoints[BUTTON_BREAKPOINT]}) {
                                font-size: 1.375rem;
                            }
                        `;
                    }
                    case Sizes.medium50: {
                        return css`
                            font-size: 1.125rem;
                        `;
                    }

                    case Sizes.small: {
                        return css`
                            font-size: 1.125rem;
                        `;
                    }
                }
            }}
        }
    }
`;

export const StyledStartIcon = styled(StyledIcon)<StyledIconProps>`
    &&& {
        ${({ theme, $size }) => {
            switch ($size) {
                case Sizes.large: {
                    return css`
                        margin-right: 12px;
                        margin-left: -6px;

                        @media (min-width: ${theme.breakpoints[BUTTON_BREAKPOINT]}) {
                            margin-right: 10px;
                            margin-left: -5px;
                        }
                    `;
                }

                case Sizes.medium: {
                    return css`
                        margin-right: 9px;
                        margin-left: -4.5px;

                        @media (min-width: ${theme.breakpoints[BUTTON_BREAKPOINT]}) {
                            margin-right: 11px;
                            margin-left: -5.5px;
                        }
                    `;
                }
                case Sizes.medium50: {
                    return css`
                        margin-right: 8px;
                        margin-left: -4px;
                    `;
                }

                case Sizes.small: {
                    return css`
                        margin-right: 6px;
                        margin-left: -3px;
                    `;
                }
            }
        }}
    }
`;

export const StyledEndIcon = styled(StyledIcon)<StyledIconProps>`
    &&& {
        ${({ theme, $size }) => {
            switch ($size) {
                case Sizes.large: {
                    return css`
                        margin-left: 12px;
                        margin-right: -6px;

                        @media (min-width: ${theme.breakpoints[BUTTON_BREAKPOINT]}) {
                            margin-left: 10px;
                            margin-right: -5px;
                        }
                    `;
                }

                case Sizes.medium: {
                    return css`
                        margin-left: 9px;
                        margin-right: -4.5px;

                        @media (min-width: ${theme.breakpoints[BUTTON_BREAKPOINT]}) {
                            margin-left: 11px;
                            margin-right: -5.5px;
                        }
                    `;
                }

                case Sizes.medium50: {
                    return css`
                        margin-left: 8px;
                        margin-right: -4px;
                    `;
                }

                case Sizes.small: {
                    return css`
                        margin-left: 6px;
                        margin-right: -3px;
                    `;
                }
            }
        }}
    }
`;

export const StyledButtonIndicator = styled.div`
    position: absolute;
    top: -5px;
    right: -5px;
`;
